import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";

const cn = (...classes) => classes.filter(Boolean).join(" ");

const Modal = ({
  children,
  onClose,
  title,
  subtitle,
  size = "lg",
  closeOnBackdrop = true,
  showCloseButton = true,
  className = "",
  contentClassName = "",
}) => {
  const titleId = useId();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
    full: "max-w-[calc(100vw-1rem)]",
  };

  const handleBackdropClick = () => {
    if (closeOnBackdrop) {
      onClose?.();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[12000] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-5"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onMouseDown={(event) => event.stopPropagation()}
        className={cn(
          "relative flex max-h-[94vh] w-full flex-col overflow-hidden rounded-t-[32px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.45)] dark:border-slate-700 dark:bg-slate-900 sm:rounded-[32px]",
          sizeClasses[size] || sizeClasses.lg,
          className,
        )}
      >
        {(title || showCloseButton) && (
          <div className="relative shrink-0 overflow-hidden border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-sky-500 to-cyan-400" />

            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {title && (
                  <h2
                    id={titleId}
                    className="truncate text-lg font-bold text-[#123B57] dark:text-white sm:text-xl"
                  >
                    {title}
                  </h2>
                )}

                {subtitle && (
                  <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                    {subtitle}
                  </p>
                )}
              </div>

              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-rose-500/40 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                  aria-label="Fermer la fenêtre"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex-1 overflow-y-auto bg-slate-50/70 p-4 dark:bg-slate-950/30 sm:p-6",
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;